// The composition artifact feeds the hover breakdown on a soilcomp column and
// the prescription's K-fixation efficiency. Series percent, clay-group percent,
// and gross K-fixation for Visualize are stack columns. This file does not
// rebuild those layers. The map-unit legend reads the stack's categories.
import type {
  FieldSoilComposition,
  ManifestLayer,
  ClayGroupKey,
  SoilMapUnitComposition,
} from "../types/contract";
import { api, ApiError } from "../api";
import { mockCovariateValues } from "./data/syntheticGrid";
import { stackCellData, stackColumns } from "./stackClient";
import {
  grossKFixation,
  kBuildupEfficiency,
  DEFAULT_KFIX_SET,
  type KFixSet,
} from "./kFixation";

const MUKEY_KEY = "ssurgo__soil_soilmapunit";
const CLAY_KEY = "ssurgo__soil_clay";

export const CLAY_GROUPS: { key: ClayGroupKey; label: string }[] = [
  { key: "kaolinite", label: "Kaolinite" },
  { key: "smectite", label: "Smectite" },
  { key: "illite", label: "Illite / mica" },
  { key: "vermiculite", label: "Vermiculite" },
  { key: "chlorite", label: "Chlorite" },
  { key: "mixed_layer", label: "Interstratified / mixed-layer" },
  { key: "oxides", label: "Fe/Al oxides" },
  { key: "quartz_other", label: "Quartz / other" },
];

// Clay groups with any non-zero value, in canonical order. The Rx availability
// check still reads the artifact; Visualize reads soilcomp__clay_* instead.
export function clayGroupsInField(
  artifact: FieldSoilComposition,
): { key: ClayGroupKey; label: string }[] {
  const present = new Set<ClayGroupKey>();
  for (const mu of Object.values(artifact.map_units)) {
    for (const g of CLAY_GROUPS)
      if ((mu.clay_groups[g.key] ?? 0) > 0) present.add(g.key);
  }
  return CLAY_GROUPS.filter((g) => present.has(g.key));
}

function dominantSeriesName(
  components: { series: string; pct: number }[] | undefined,
): string | null {
  if (!components?.length) return null;
  const top = [...components].sort((a, b) => b.pct - a.pct)[0];
  return top?.series ? top.series : null;
}

export function mukeyLabels(
  artifact: FieldSoilComposition,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const [mukey, mu] of Object.entries(artifact.map_units)) {
    const dominant = dominantSeriesName(mu.components);
    const label = (mu.muname && mu.muname.trim()) || dominant;
    if (label) out.set(mukey, label);
  }
  return out;
}

export function cellBreakdown(mu: SoilMapUnitComposition | undefined): string {
  if (!mu || !mu.components?.length) return "";
  const parts = [...mu.components]
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 6)
    .map((c) => `${Math.round(c.pct)}% ${c.series}`);
  return `${mu.muname ? `${mu.muname}: ` : ""}${parts.join("; ")}`;
}

export function applyCompositionBreakdown(
  features: { properties: Record<string, unknown> }[],
  values: (number | null)[],
  mukeys: string[],
  artifact: FieldSoilComposition,
): void {
  let featureIndex = 0;
  for (let i = 0; i < values.length && featureIndex < features.length; i++) {
    const value = values[i];
    if (value == null || !Number.isFinite(value)) continue;
    const text = cellBreakdown(artifact.map_units[mukeys[i] ?? ""]);
    if (text) features[featureIndex]!.properties.breakdown = text;
    featureIndex += 1;
  }
}

// MOCK-mode cell -> mukey assignment: three diagonal bands over the synthetic grid.
export function assignMockMukeys(
  centroids: { lng: number; lat: number }[],
  mukeys: string[],
): string[] {
  if (!mukeys.length) return centroids.map(() => "");
  const lngs = centroids.map((c) => c.lng);
  const lats = centroids.map((c) => c.lat);
  const minLng = Math.min(...lngs),
    maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats),
    maxLat = Math.max(...lats);
  const spanLng = maxLng - minLng || 1;
  const spanLat = maxLat - minLat || 1;
  return centroids.map((c) => {
    const t = ((c.lng - minLng) / spanLng + (c.lat - minLat) / spanLat) / 2;
    return mukeys[Math.min(mukeys.length - 1, Math.floor(t * mukeys.length))];
  });
}

interface KFixJoin {
  artifact: FieldSoilComposition;
  // Covariate-stack content_version the cell arrays were read against.
  stackContentVersion: string;
  cellMukeys: string[];
  cellClay: (number | null)[];
}

// One entry per field. cellMukeys and cellClay are indexed in stack cell order.
// A stack rebuild can reorder or resize those cells while the composition
// artifact version stays the same, so a hit requires both content versions
// and a cellMukeys length equal to the current grid cell count.
const joinCache = new Map<string, KFixJoin>();

export function resetSoilJoinCacheForTests(): void {
  joinCache.clear();
}

const isNotFound = (e: unknown) =>
  e instanceof ApiError && e.code === "ERR_NOT_FOUND";

type StackGrid = Awaited<ReturnType<typeof stackCellData>>;

function joinCacheHit(
  cached: KFixJoin | undefined,
  artifactVersion: string,
  stackVersion: string,
  cellCount: number,
): cached is KFixJoin {
  return (
    cached != null &&
    cached.artifact.content_version === artifactVersion &&
    cached.stackContentVersion === stackVersion &&
    cached.cellMukeys.length === cellCount
  );
}

async function readCellJoin(
  fieldId: string,
  artifact: FieldSoilComposition,
  cell: StackGrid,
): Promise<Pick<KFixJoin, "cellMukeys" | "cellClay"> | null> {
  if (import.meta.env.MODE === "test") {
    return {
      cellMukeys: assignMockMukeys(
        cell.centroids,
        Object.keys(artifact.map_units),
      ),
      cellClay: mockCovariateValues(cell.centroids, 12, 42),
    };
  }
  const { columns } = await stackColumns(fieldId, [MUKEY_KEY, CLAY_KEY]);
  // stackColumns is a second stack read. A rebuild between the two reads gives
  // columns for a different cell set, so refuse them instead of indexing.
  const mk = columns[MUKEY_KEY] ?? cell.geoms.map(() => null);
  const clay = columns[CLAY_KEY] ?? cell.geoms.map(() => null);
  if (mk.length !== cell.grid.n || clay.length !== cell.grid.n) return null;
  const after = await stackCellData(fieldId, []);
  if (after.grid.contentVersion !== cell.grid.contentVersion) return null;
  return {
    cellMukeys: mk.map((v) => (v == null ? "" : String(v))),
    cellClay: clay,
  };
}

async function loadKFixJoin(fieldId: string): Promise<KFixJoin | null> {
  let artifact: FieldSoilComposition;
  try {
    artifact = await api.fetchSoilComposition(fieldId);
  } catch (e) {
    if (isNotFound(e)) return null;
    throw e;
  }
  // grid.contentVersion is the covariate stack content_version. stackClient
  // copies it onto the decoded stack and busts that decode when it changes.
  const cell = await stackCellData(fieldId, []);
  const stackVersion = cell.grid.contentVersion;
  const cellCount = cell.grid.n;
  const cached = joinCache.get(fieldId);
  if (joinCacheHit(cached, artifact.content_version, stackVersion, cellCount))
    return cached;

  const cells = await readCellJoin(fieldId, artifact, cell);
  // A fresh read that is not the same cell set as this grid is not indexable.
  if (!cells || cells.cellMukeys.length !== cellCount) return null;
  const join: KFixJoin = {
    artifact,
    stackContentVersion: stackVersion,
    cellMukeys: cells.cellMukeys,
    cellClay: cells.cellClay,
  };
  joinCache.set(fieldId, join);
  return join;
}

export async function perCellKFixEfficiency(
  fieldId: string,
  set: KFixSet = DEFAULT_KFIX_SET,
  release?: number,
): Promise<(number | null)[]> {
  const join = await loadKFixJoin(fieldId);
  if (!join) return [];
  return join.cellMukeys.map((mk, i) => {
    const mu = join.artifact.map_units[mk];
    return mu
      ? kBuildupEfficiency(
          grossKFixation(mu.clay_groups, join.cellClay[i], set),
          release,
        )
      : null;
  });
}

export async function compositionBreakdownFor(
  fieldId: string,
  key: string,
  layer: ManifestLayer,
): Promise<ManifestLayer> {
  if (import.meta.env.MODE === "test") return layer;
  try {
    const join = await loadKFixJoin(fieldId);
    if (!join) return layer;
    const { features, grid } = await stackCellData(fieldId, [key]);
    // The values come from a third stack read and the layer's features from
    // the read that drew it. Skip the breakdown unless all of them are the same
    // cell set the join was built against.
    if (
      layer.content_version !== join.stackContentVersion ||
      grid.contentVersion !== join.stackContentVersion ||
      grid.n !== join.cellMukeys.length
    )
      return layer;
    const inline = layer.inline as {
      features?: { properties: Record<string, unknown> }[];
    } | null;
    if (!inline?.features) return layer;
    applyCompositionBreakdown(
      inline.features,
      features[0] ?? [],
      join.cellMukeys,
      join.artifact,
    );
    return layer;
  } catch {
    return layer;
  }
}
